import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import MyTable from '../../../utils/MyTable';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Pie } from 'react-chartjs-2';

import { ReqsComdinheiroAPI } from '../../../../API/reqs_comdinheiro';
import { unoTheme } from '../../../../assets/styles/unoTheme';
import { useDispatch } from 'react-redux';
import allActions from '../../../../actions';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
    },
    tableWrapper: {
        flex: 1,
        overflow: 'auto',
    },
    chartWrapper: {
        width: '40%',
        maxWidth: '40%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        padding: theme.spacing(2),
        backgroundColor: 'white',
        overflowY: 'auto',
    },
    chartTitle: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: unoTheme.defaultTextColor,
        marginBottom: theme.spacing(1),
    },
    chartTotal: {
        fontSize: '13px',
        fontWeight: 'bold',
        color: unoTheme.defaultTextColor,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    legendList: {
        width: '100%',
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '3px 0',
        fontSize: '12px',
        color: unoTheme.defaultTextColor,
        borderBottom: `1px solid ${unoTheme.separatorTableRow}`,
    },
    legendLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        overflow: 'hidden',
    },
    legendDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    legendCount: {
        fontWeight: 'bold',
        color: unoTheme.mainColor,
        flexShrink: 0,
        marginLeft: theme.spacing(1),
    },
}));

function buildPieData(groupedData) {
    const labels = groupedData.map(({ req_name }) => req_name);
    const data = groupedData.map(({ count }) => Number(count));
    const backgroundColor = labels.map(
        (_, i) => unoTheme.chartColors[i % unoTheme.chartColors.length]
    );
    return {
        labels,
        datasets: [{ data, backgroundColor }],
    };
}

const pieOptions = {
    legend: {
        display: false,
    },
    tooltips: {
        callbacks: {
            label: function (tooltipItem, data) {
                const label = data.labels[tooltipItem.index];
                const value = data.datasets[0].data[tooltipItem.index];
                return ` ${label}: ${value}`;
            },
        },
    },
};

export default function ComdinheiroReqsLogs({ dateStart, dateEnd, userSearch }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [logs, setLogs] = useState([]);
    const [groupedData, setGroupedData] = useState([]);

    const LIMIT = 30;
    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);

    const handlePage = (value) => {
        const newPage = page + value;
        setPage(newPage);
        loadLogs(newPage);
    };

    async function loadLogs(currentPage = 1) {
        dispatch(allActions.innerLoadActions.setInnerLoad({
            show: true,
            loadMessage: 'Carregando logs...',
        }));

        const offset = (currentPage - 1) * LIMIT;
        const response = await ReqsComdinheiroAPI.getDetailLogs(offset, LIMIT, dateStart, dateEnd, userSearch);

        if (response.success && response.body) {
            const rows = response.body.rows;
            if (rows && rows.length > 0) {
                setLogs(rows);
                setCount(response.body.totalRows);
                setGroupedData(response.body.groupedData || []);
                dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));
            } else {
                setLogs([]);
                setCount(0);
                setGroupedData([]);
                dispatch(allActions.innerLoadActions.setInnerLoad({
                    show: true,
                    emptyMessage: 'Não existem logs de requisições',
                }));
            }
        } else {
            console.error('RESPONSE ERROR: ', response.body);
            dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));
        }
    }

    useEffect(() => {
        setPage(1);
        loadLogs(1);
    }, [dateStart, dateEnd, userSearch]);

    const pieData = buildPieData(groupedData);
    const totalReqs = groupedData.reduce((sum, { count }) => sum + Number(count), 0);

    return (
        <div className={classes.root}>
            <div className={`${classes.tableWrapper} defaultScrollWithHeaderContent`}>
                <MyTable
                    stickyHeader
                    nofixed={true}
                    size="small"
                    pagination={logs && logs.length > 0}
                    itemspage={LIMIT}
                    totalitems={count}
                    currentPage={page}
                    handlePage={handlePage}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell align="left" style={{ width: '160px', maxWidth: '160px' }}>Usuário</TableCell>
                            <TableCell align="left" style={{ whiteSpace: 'nowrap' }}>Requisição</TableCell>
                            <TableCell align="left" style={{ width: '100%' }}>Data</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.map((row, index) => (
                            <TableRow key={'comdinheiro-log-' + index}>
                                <TableCell align="left" style={{ width: '160px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.username.toUpperCase()}</TableCell>
                                <TableCell align="left" style={{ whiteSpace: 'nowrap' }}>{row.req_name}</TableCell>
                                <TableCell align="left" style={{ color: unoTheme.mainColor, width: '100%', fontWeight: 'bold' }}>
                                    {moment(row.created_date).format('DD/MM/YYYY - HH:mm:ss')}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </MyTable>
            </div>
            {groupedData.length > 0 && (
                <div className={classes.chartWrapper}>
                    <span className={classes.chartTitle}>Requisições por tipo</span>
                    <Pie data={pieData} options={pieOptions} redraw={true} />
                    <span className={classes.chartTotal}>Total: {totalReqs} requisições</span>
                    <ul className={classes.legendList}>
                        {groupedData.map(({ req_name, count }, i) => (
                            <li key={'legend-' + i} className={classes.legendItem}>
                                <span className={classes.legendLabel}>
                                    <span
                                        className={classes.legendDot}
                                        style={{ backgroundColor: unoTheme.chartColors[i % unoTheme.chartColors.length] }}
                                    />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {req_name}
                                    </span>
                                </span>
                                <span className={classes.legendCount}>{Number(count)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
